import { useState, useMemo, Fragment } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ChevronRight, Download, Eye, Loader2 } from 'lucide-react';
import {
  Button,
  Checkbox,
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeader,
  DataTableRow,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Text,
  cn,
  useToast,
} from '@musa.tech/design-system';
import { useAppSelector } from '@/store/hooks';
import { fetchPlantonCertificate, downloadPlantonCertificatesBulk, type PlantonCertificate } from '@/integrations/agreements/client';
import { isInternal } from '@/utils/keycloakRoles';
import { useFilterOptions } from '@/hooks/useFilterOptions';
import { usePlantonCertificates } from '@/hooks/usePlantonCertificates';
import { MultiSelect } from '@/components/dashboard/MultiSelect';
import { EstablishmentMultiSelect } from '@/components/dashboard/EstablishmentMultiSelect';

const certKey = (c: PlantonCertificate) => `${c.generator_id}-${c.ano}-${c.mes}`;

const formatPeriod = (mes: number, ano: number) => {
  const s = format(new Date(ano, mes - 1, 1), "MMMM 'de' yyyy", { locale: ptBR });
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export function CertificadosTab() {
  const { toast } = useToast();
  const internal = isInternal();

  const [selectedGeneratorExtIds, setSelectedGeneratorExtIds] = useState<string[]>([]);
  const [selectedEstablishmentIds, setSelectedEstablishmentIds] = useState<string[]>([]);

  const storeGenerators = useAppSelector((s) => s.establishmentAccess.generators);
  const storeEstablishments = useAppSelector((s) => s.establishmentAccess.establishments);

  const { generators: availableGenerators, establishments: availableEstablishments } = useFilterOptions({
    selectedGeneratorExternalIds: selectedGeneratorExtIds,
  });
  const generatorSelectOptions = useMemo(
    () => availableGenerators.map((g) => ({ value: g.generator_id, label: g.generator_name })),
    [availableGenerators],
  );
  const establishmentSelectOptions = useMemo(
    () => availableEstablishments.map((e) => ({ value: e.id_new_engine_generators, label: e.establishment_name })),
    [availableEstablishments],
  );

  const { certificates, isLoading } = usePlantonCertificates(selectedGeneratorExtIds, selectedEstablishmentIds);

  const getGeneratorName = (generatorId: string) =>
    storeGenerators.find((g) => g.id === generatorId)?.name
    ?? storeEstablishments.find((e) => e.id === generatorId)?.name
    ?? generatorId;

  const availableYears = [...new Set(certificates.map((c) => c.ano))].sort((a, b) => b - a);
  const availableGeneratorIds = [...new Set(certificates.map((c) => c.generator_id))];
  const hasMultipleGenerators = availableGeneratorIds.length > 1;

  const [selectedYear, setSelectedYear] = useState<string>('all');
  const [selectedMonth, setSelectedMonth] = useState<string>('all');
  const [selectedGenerator, setSelectedGenerator] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);
  const [previewCert, setPreviewCert] = useState<PlantonCertificate | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const availableMonths = useMemo(() => {
    const base = selectedYear === 'all' ? certificates : certificates.filter((c) => c.ano === Number(selectedYear));
    return [...new Set(base.map((c) => c.mes))].sort((a, b) => a - b);
  }, [certificates, selectedYear]);

  const filtered = certificates
    .filter((c) => selectedYear === 'all' || c.ano === Number(selectedYear))
    .filter((c) => selectedMonth === 'all' || c.mes === Number(selectedMonth))
    .filter((c) => selectedGenerator.length === 0 || selectedGenerator.includes(c.generator_id));

  const groups = useMemo(() => {
    const generatorIds = [...new Set(filtered.map((c) => c.generator_id))];
    return generatorIds.map((gid) => ({
      generatorId: gid,
      certs: filtered.filter((c) => c.generator_id === gid),
    }));
  }, [filtered]);

  const allFilteredSelected = filtered.length > 0 && filtered.every((c) => selected.has(certKey(c)));
  const someFilteredSelected = filtered.some((c) => selected.has(certKey(c)));

  const toggleAll = () => {
    setSelected(allFilteredSelected ? new Set() : new Set(filtered.map(certKey)));
  };

  const toggle = (c: PlantonCertificate) => {
    const key = certKey(c);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { next.delete(key); } else { next.add(key); }
      return next;
    });
  };

  const toggleGroupSelection = (groupCerts: PlantonCertificate[]) => {
    const allSelected = groupCerts.every((c) => selected.has(certKey(c)));
    setSelected((prev) => {
      const next = new Set(prev);
      groupCerts.forEach((c) => {
        if (allSelected) { next.delete(certKey(c)); } else { next.add(certKey(c)); }
      });
      return next;
    });
  };

  const toggleGroupExpand = (gid: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) { next.delete(gid); } else { next.add(gid); }
      return next;
    });
  };

  const handlePreview = async (cert: PlantonCertificate, e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewCert(cert);
    setIsLoadingPreview(true);
    setPreviewUrl(null);
    try {
      const result = await fetchPlantonCertificate(Number(cert.generator_id), cert.ano, cert.mes);
      setPreviewUrl(result.signed_url ?? null);
    } catch {
      toast({ title: 'Erro ao carregar certificado', variant: 'error' });
      setPreviewCert(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleDownload = async () => {
    const toDownload = filtered.filter((c) => selected.has(certKey(c)));
    if (!toDownload.length) return;
    setIsDownloading(true);
    try {
      const blob = await downloadPlantonCertificatesBulk(
        toDownload.map((c) => ({ generator_id: Number(c.generator_id), ano: c.ano, mes: c.mes })),
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'certificados.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Erro ao baixar certificados', variant: 'error' });
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        {internal && (
          <>
            <MultiSelect
              options={generatorSelectOptions}
              value={selectedGeneratorExtIds}
              onChange={setSelectedGeneratorExtIds}
              placeholder="Selecione geradores..."
              searchPlaceholder="Buscar gerador..."
              className="w-56"
              hideBadges
            />
            <EstablishmentMultiSelect
              options={establishmentSelectOptions}
              value={selectedEstablishmentIds}
              onChange={setSelectedEstablishmentIds}
              placeholder="Selecione estabelecimentos..."
              className="w-56"
              hideBadges
            />
          </>
        )}

        {hasMultipleGenerators && !internal && (
          <MultiSelect
            options={availableGeneratorIds.map((gid) => ({ value: gid, label: getGeneratorName(gid) }))}
            value={selectedGenerator}
            onChange={setSelectedGenerator}
            placeholder="Todos os geradores"
            searchPlaceholder="Buscar gerador..."
            className="w-56"
            hideBadges
          />
        )}

        <Select value={selectedYear} onValueChange={(v) => { setSelectedYear(v); setSelectedMonth('all'); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Ano" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os anos</SelectItem>
            {availableYears.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="Mês" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os meses</SelectItem>
            {availableMonths.map((m) => (
              <SelectItem key={m} value={String(m)}>
                {format(new Date(2000, m - 1, 1), 'MMMM', { locale: ptBR }).replace(/^\w/, (c) => c.toUpperCase())}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto">
          <Button
            variant="default"
            size="sm"
            disabled={selected.size === 0 || isDownloading}
            onClick={handleDownload}
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Baixar selecionados ({selected.size})
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-border overflow-hidden">
        <DataTable>
          <DataTableHeader>
            <DataTableRow>
              <DataTableHead className="w-10">
                <Checkbox
                  checked={allFilteredSelected ? true : someFilteredSelected ? 'indeterminate' : false}
                  onCheckedChange={toggleAll}
                />
              </DataTableHead>
              <DataTableHead>Selecionar todos</DataTableHead>
              <DataTableHead className="w-10" />
            </DataTableRow>
          </DataTableHeader>
          <DataTableBody>
            {internal && selectedGeneratorExtIds.length === 0 && selectedEstablishmentIds.length === 0 ? (
              <DataTableRow>
                <DataTableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Selecione pelo menos um gerador para ver os certificados.
                </DataTableCell>
              </DataTableRow>
            ) : filtered.length === 0 ? (
              <DataTableRow>
                <DataTableCell colSpan={3} className="text-center text-muted-foreground py-8">
                  Nenhum certificado encontrado para os filtros selecionados.
                </DataTableCell>
              </DataTableRow>
            ) : (
              groups.map(({ generatorId, certs }) => {
                const isExpanded = expandedGroups.has(generatorId);
                const allGroupSelected = certs.every((c) => selected.has(certKey(c)));
                const someGroupSelected = certs.some((c) => selected.has(certKey(c)));

                return (
                  <Fragment key={generatorId}>
                    <DataTableRow
                      className="cursor-pointer bg-table-header"
                      onClick={() => toggleGroupExpand(generatorId)}
                    >
                      <DataTableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={allGroupSelected ? true : someGroupSelected ? 'indeterminate' : false}
                          onCheckedChange={() => toggleGroupSelection(certs)}
                        />
                      </DataTableCell>
                      <DataTableCell>
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={cn(
                              'w-4 h-4 text-neutral-500 transition-transform duration-200',
                              isExpanded && 'rotate-90',
                            )}
                          />
                          <Text variant="label" size="small">
                            {getGeneratorName(generatorId)}
                          </Text>
                          <Text variant="body" size="xsmall" className="text-muted-foreground">
                            {certs.length} {certs.length === 1 ? 'certificado' : 'certificados'}
                          </Text>
                        </div>
                      </DataTableCell>
                      <DataTableCell />
                    </DataTableRow>

                    {isExpanded &&
                      certs.map((cert) => {
                        const key = certKey(cert);
                        return (
                          <DataTableRow
                            key={key}
                            className="cursor-pointer"
                            onClick={() => toggle(cert)}
                          >
                            <DataTableCell onClick={(e) => e.stopPropagation()}>
                              <Checkbox
                                checked={selected.has(key)}
                                onCheckedChange={() => toggle(cert)}
                              />
                            </DataTableCell>
                            <DataTableCell>
                              <Text variant="body" size="small" className="pl-6">
                                {formatPeriod(cert.mes, cert.ano)}
                              </Text>
                            </DataTableCell>
                            <DataTableCell onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="icon" onClick={(e) => handlePreview(cert, e)}>
                                <Eye className="w-4 h-4 text-neutral-500" />
                              </Button>
                            </DataTableCell>
                          </DataTableRow>
                        );
                      })}
                  </Fragment>
                );
              })
            )}
          </DataTableBody>
        </DataTable>
      </div>

      <Dialog
        open={previewCert !== null}
        onOpenChange={(open) => {
          if (!open) { setPreviewCert(null); setPreviewUrl(null); }
        }}
      >
        <DialogContent className="w-[95vw] max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {previewCert ? formatPeriod(previewCert.mes, previewCert.ano) : ''}
            </DialogTitle>
          </DialogHeader>
          <div className="h-[75vh]">
            {isLoadingPreview ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-8 h-8 animate-spin text-neutral-400" />
              </div>
            ) : previewUrl ? (
              <iframe src={previewUrl} className="w-full h-full" title="Certificado" />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
